import { useEffect, useRef, useState } from "react";
import "./PortraitFlip.css";

export default function PortraitFlip({ images }) {
  const [portrait, setPortrait] = useState({ index: 0, turns: 0, faces: [0, 1] });
  const changing = useRef(false);
  const mounted = useRef(false);
  const pointerType = useRef("mouse");
  const canChange = images.length > 1;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Unlock even if the browser cancels the CSS transition (for example when
  // reduced motion is enabled while a flip is running).
  useEffect(() => {
    if (!portrait.turns) return;
    const timeout = setTimeout(() => { changing.current = false; }, 800);
    return () => clearTimeout(timeout);
  }, [portrait.turns]);

  const showNext = async () => {
    if (!canChange || changing.current) return;
    changing.current = true;
    const next = (portrait.index + 1) % images.length;
    // Decode before turning so a new photo never flashes blank.
    const image = new Image();
    image.src = images[next].src;
    try {
      await image.decode();
      if (!mounted.current) return;
      const faces = [...portrait.faces];
      faces[(portrait.turns + 1) % 2] = next;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setPortrait({ index: next, turns: portrait.turns + 1, faces });
      if (reduceMotion) changing.current = false;
    } catch {
      // Keep the current portrait if an image cannot be loaded.
      changing.current = false;
    }
  };

  return (
    <button
      type="button"
      className="portrait-flip"
      aria-label={canChange ? "Show next portrait of Facundo Criscuolo" : images[0].alt}
      disabled={!canChange}
      onPointerEnter={event => {
        pointerType.current = event.pointerType;
        if (event.pointerType === "mouse") showNext();
      }}
      onPointerDown={event => { pointerType.current = event.pointerType; }}
      onClick={event => {
        if (event.detail === 0 || pointerType.current !== "mouse") showNext();
      }}
    >
      <span
        className="portrait-flip__card"
        style={{ transform: `rotateY(${portrait.turns * 180}deg)` }}
        onTransitionEnd={event => {
          if (event.target === event.currentTarget && event.propertyName === "transform") {
            changing.current = false;
          }
        }}
      >
        {portrait.faces.map((index, face) => (
          <img
            key={face}
            className={`portrait-flip__face ${face ? "portrait-flip__face--back" : ""}`}
            src={images[index % images.length].src}
            alt={portrait.turns % 2 === face ? images[portrait.index].alt : ""}
            aria-hidden={portrait.turns % 2 !== face}
            decoding="async"
          />
        ))}
      </span>
      {canChange && <span className="portrait-flip__hint" aria-hidden="true">Hover or tap to flip <span>↻</span></span>}
    </button>
  );
}
